import useFocusTrap from '@charlietango/use-focus-trap';
import React, {
  ComponentProps,
  FC,
  HTMLAttributes,
  ReactNode,
  useCallback,
} from 'react';
import styled from 'styled-components';

import { cornerRadiusPx } from '#/service/constants';

import { ReactPortal } from '../Portal';
import { CloseButton } from './CloseButton';
import { FullScreenBackground } from './styled';

export interface ModalProps extends HTMLAttributes<HTMLDialogElement> {
  /** Determines if modal should be visible. Please note that you should not be using `isOpen` for modals with state. In such cases you'd rather use @code `{shouldShowModal && <YourModal />}` instead. */
  isOpen: boolean;

  onClose?: () => void;

  /**
   * If `true`, the modal will close when user clicks overlay
   * @default false
   */
  closeOnOverlayClick?: boolean;

  /**
   * If `true`, the modal will close when the `Esc` key is pressed
   * @default true
   */
  closeOnEsc?: boolean;

  /**
   * If `true`, there will be a close button in the top right part of the modal
   * @default true
   */
  showCloseButton?: boolean;

  /** Children will be included between the message and the close button */
  children?: ReactNode;

  /** Custom aria role used by the modal. @default dialog */
  role?: 'dialog' | 'alert' | 'alertdialog';

  /** @default 'theme' Set to 'static' to use a static shadow background color that is theme-independent */
  overlayBackgroundType?: 'theme' | 'static';

  /** @default undefined Set a custom background color for the overlay */
  overlayCustomBackgroundColor?: string;

  /** @default 'solid' Set to 'transparent` to remove the background of the modal */
  modalBackgroundType?: 'solid' | 'transparent';
}

interface ModalComponent extends FC<ModalProps> {
  Title: FC<ComponentProps<'h2'>>;
}

export const Modal: ModalComponent = ({
  isOpen,
  children,
  showCloseButton = true,
  onClose,
  closeOnOverlayClick = false,
  closeOnEsc = true,
  role = 'dialog',
  overlayBackgroundType = 'theme',
  overlayCustomBackgroundColor,
  modalBackgroundType = 'solid',
  ...rest
}) => {
  const ref = useFocusTrap();

  const onKeyDown = useCallback(
    (ev: React.KeyboardEvent) => {
      if (ev.key === 'Escape' && closeOnEsc && onClose) {
        ev.stopPropagation();
        onClose();
      }
    },
    [closeOnEsc, onClose]
  );

  const onOverlayClick = useCallback(
    (ev: React.MouseEvent<HTMLDivElement>) => {
      const target = ev.target as HTMLDivElement;

      if (
        closeOnOverlayClick &&
        target instanceof HTMLDivElement &&
        target.dataset['backdrop'] &&
        onClose
      ) {
        ev.stopPropagation();
        onClose();
      }
    },
    [onClose, closeOnOverlayClick]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <ReactPortal>
      <StyledOverlay
        $modalBackgroundType={modalBackgroundType}
        $overlayCustomBackgroundColor={overlayCustomBackgroundColor}
        $overlayType={overlayBackgroundType}
        data-backdrop
        data-nosnippet
        data-testid="modal-backdrop"
        onClick={onOverlayClick}
        onKeyDown={onKeyDown}
        ref={ref}
      >
        <ModalBody
          $modalBackgroundType={modalBackgroundType}
          aria-describedby="modal-description"
          aria-labelledby="modal-title"
          aria-modal={true}
          role={role}
          {...rest}
        >
          {showCloseButton && <CloseButton onClick={onClose} />}
          <div className="modal-body" data-testid="modal-body">
            {children}
          </div>
        </ModalBody>
      </StyledOverlay>
    </ReactPortal>
  );
};

interface OverlayProps {
  $overlayType: ModalProps['overlayBackgroundType'];
  $overlayCustomBackgroundColor: ModalProps['overlayCustomBackgroundColor'];
  $modalBackgroundType: ModalProps['modalBackgroundType'];
}

const getOverlayBackground = ({
  $modalBackgroundType,
  $overlayType,
  $overlayCustomBackgroundColor,
}: OverlayProps) => {
  if ($overlayType === 'static' && $modalBackgroundType === 'solid') {
    return 'var(--overlayBackdrop)';
  }

  if ($overlayCustomBackgroundColor) {
    return $overlayCustomBackgroundColor;
  }

  return 'var(--overlayBackdropDarker)';
};

const StyledOverlay = styled(FullScreenBackground)<OverlayProps>`
  background: ${(props) => getOverlayBackground(props)};
`;

interface ModalBodyProps {
  $modalBackgroundType: ModalProps['modalBackgroundType'];
}

const ModalBody = styled.dialog<ModalBodyProps>`
  /* reset dialog's default styles */
  all: unset;
  display: block;
  height: auto;
  background-color: ${({ $modalBackgroundType }) =>
    $modalBackgroundType === 'solid'
      ? 'var(--pageSecondaryBackground)'
      : 'none'};
  color: var(--pageParagraphForeground);
  text-align: center;
  border-radius: ${({ theme }) => cornerRadiusPx[theme.cornerRadius]}px;
  position: relative;
  z-index: 100;
  padding: 1.5rem;
`;

Modal.Title = styled.h2.attrs(() => ({
  id: 'modal-title',
}))`
  /* empty for now */
`;
